import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@design-system/components/alert-dialog";
import { Button } from "@design-system/components/button";
import { Text } from "@design-system/components/Text";

import {
  FileText,
  AlertCircle,
  Loader2,
  Trash2,
  XCircle,
} from "lucide-react";

import { Badge } from "@design-system/components/badge";
import type {
  ContextMaterialFileItem,
  ContextMaterialsFilesResponse,
  ContextMaterialsFilesListResponse,
} from "@digital-twins/shared-types";

export interface ContextMaterialsStepCardProps {
  files: ContextMaterialFileItem[]
  onFilesChange: (files: ContextMaterialFileItem[]) => void
  onUpload: (file: File) => Promise<ContextMaterialsFilesResponse>
  onRemove: (fileId: string) => Promise<ContextMaterialsFilesResponse>
  isUploading: boolean
  isRemoving: boolean
  refreshToken?: number
  listFiles: () => Promise<ContextMaterialsFilesListResponse>
}
import { FileUploadZone } from "@/components/upload/FileUploadZone";

const ACCEPTED_CONTEXT_TYPES =
  ".txt,.csv,.html,.htm,.json,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

function extractErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== "object") return fallback;
  const obj = err as Record<string, unknown>;
  const response = obj.response as Record<string, unknown> | undefined;
  const data = response?.data as Record<string, unknown> | undefined;
  const msg = data?.message ?? data?.error;
  if (typeof msg === "string" && msg.trim()) return msg;
  if (typeof obj.message === "string" && obj.message.trim()) return obj.message;
  return fallback;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadingStatusBadge() {
  const { t } = useTranslation("app");
  return (
    <Badge
      variant="outline"
      className="gap-1 rounded-full border-foreground/20 bg-foreground/5 text-foreground/60 dark:border-white/20 dark:bg-white/5 dark:text-white/60 text-xs"
      data-testid="context-file-uploading-badge"
    >
      <Loader2 className="h-3 w-3 animate-spin" />
      {t("audienceCreate.uploading")}
    </Badge>
  );
}

function FileStatusBadge({ file }: { file: ContextMaterialFileItem }) {
  const { t } = useTranslation("app");
  // Pending means queued for create-time indexing — not an in-flight upload.
  if (file.indexingStatus !== "failed") {
    return null;
  }
  return (
    <Badge
      variant="outline"
      className="gap-1 rounded-full border-destructive/30 bg-destructive/10 text-destructive text-xs"
    >
      <XCircle className="h-3 w-3" />
      {t("audienceCreate.indexFailed")}
    </Badge>
  );
}

export function ContextMaterialsStepCard({
  files,
  onFilesChange,
  onUpload,
  onRemove,
  isUploading,
  isRemoving,
  refreshToken,
  listFiles,
}: ContextMaterialsStepCardProps) {
  const { t } = useTranslation("app");
  const [removeTarget, setRemoveTarget] =
    useState<ContextMaterialFileItem | null>(null);
  const [filesLoading, setFilesLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const listFetchEpochRef = useRef(0);
  const lastAppliedRefreshTokenRef = useRef<number | null>(null);
  const latestFilesFromPropsRef = useRef<ContextMaterialFileItem[]>(files);

  latestFilesFromPropsRef.current = files;

  const loadFilesInitial = useCallback(async () => {
    const epoch = ++listFetchEpochRef.current;
    try {
      setFilesLoading(true);
      const res = await listFiles();
      if (epoch !== listFetchEpochRef.current) {
        return;
      }
      const next = res.files ?? [];
      // Avoid clobbering mutation-seeded rows when GET lags right after enter (ContextMaterialsStep first pick).
      if (next.length === 0 && latestFilesFromPropsRef.current.length > 0) {
        return;
      }
      onFilesChange(next);
    } catch (err) {
      if (epoch !== listFetchEpochRef.current) {
        return;
      }
      toast.error(extractErrorMessage(err, t("audienceCreate.failedLoadFiles")));
      onFilesChange([]);
    } finally {
      setFilesLoading(false);
    }
  }, [listFiles, onFilesChange, t]);

  const refreshFiles = useCallback(async () => {
    const epoch = ++listFetchEpochRef.current;
    try {
      const res = await listFiles();
      if (epoch !== listFetchEpochRef.current) {
        return;
      }
      onFilesChange(res.files ?? []);
    } catch (err) {
      if (epoch !== listFetchEpochRef.current) {
        return;
      }
      toast.error(extractErrorMessage(err, t("audienceCreate.failedRefreshFiles")));
    }
  }, [listFiles, onFilesChange, t]);

  useEffect(() => {
    void loadFilesInitial();
  }, [loadFilesInitial]);

  useEffect(() => {
    if (refreshToken == null) {
      lastAppliedRefreshTokenRef.current = null;
      return;
    }
    const prev = lastAppliedRefreshTokenRef.current;
    if (prev === refreshToken) {
      return;
    }
    lastAppliedRefreshTokenRef.current = refreshToken;
    // Always refetch when the session version changes (including first non-null token).
    // Skipping the first token left the list empty if the initial GET raced the upload.
    void refreshFiles();
  }, [refreshFiles, refreshToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshFiles();
      }
    };
    const handleFocus = () => {
      void refreshFiles();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshFiles]);

  const handleFilesSelected = useCallback(
    async (fileList: FileList) => {
      if (isUploading) return;
      const toUpload = Array.from(fileList);
      if (toUpload.length === 0) return;
      // Show all files as uploading immediately before any network requests
      setUploadingFiles(toUpload);
      let successCount = 0;
      for (const file of toUpload) {
        try {
          const res = await onUpload(file);
          onFilesChange(res.files ?? []);
          successCount += 1;
        } catch (err) {
          toast.error(
            `${file.name}: ${extractErrorMessage(err, t("audienceCreate.uploadFailed"))}`,
          );
        } finally {
          setUploadingFiles((prev) => prev.filter((f) => f !== file));
        }
      }
      if (successCount > 0) {
        toast.success(
          successCount === toUpload.length
            ? t("audienceCreate.filesUploaded")
            : t("audienceCreate.filesUploadedPartial", { count: successCount }),
        );
      }
    },
    [isUploading, onUpload, onFilesChange, t],
  );

  const handleRemoveClick = useCallback((file: ContextMaterialFileItem) => {
    setRemoveTarget(file);
  }, []);

  const confirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    try {
      const res = await onRemove(removeTarget.id);
      onFilesChange(res.files ?? []);
      setRemoveTarget(null);
      toast.success(t("audienceCreate.fileRemoved"));
    } catch (err) {
      toast.error(extractErrorMessage(err, t("audienceCreate.failedRemoveFile")));
    }
  }, [removeTarget, onRemove, onFilesChange, t]);

  return (
    <>
      <div className="flex flex-col gap-8">
        {/* Upload zone — always visible, no flash */}
        <div className="rounded-xl border-dashed-6">
          <FileUploadZone
            variant="context"
            accept={ACCEPTED_CONTEXT_TYPES}
            multiple
            disabled={isUploading}
            onFilesSelected={(files) => void handleFilesSelected(files)}
          />
        </div>

        {/* Files list — skeleton while loading, card when ready */}
        {filesLoading &&
        files.length === 0 &&
        uploadingFiles.length === 0 ? (
          <div className="h-20 w-full animate-pulse rounded-xl bg-card" />
        ) : (files.length > 0 || uploadingFiles.length > 0) ? (
          <div className="flex flex-col gap-5 rounded-xl bg-card px-6 py-7">
            <p className="text-base font-medium leading-[1.6] text-card-foreground shrink-0">
              {t("audienceCreate.contextFiles")}
            </p>
            <ul className="flex flex-col divide-y divide-border">
              {/* Uploading rows — appear immediately for the whole selection */}
              {uploadingFiles.map((file, idx) => (
                <li
                  key={`uploading-${file.name}-${file.size}-${idx}`}
                  className="py-5 first:pt-0"
                  data-testid={`context-file-uploading-row-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-background dark:bg-[hsl(var(--card-inset))]">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <p className="truncate text-base leading-[1.6] text-foreground dark:text-white">
                        {file.name}
                      </p>
                      <p className="text-sm leading-[1.5] text-muted-foreground">
                        {formatBytes(file.size)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <UploadingStatusBadge />
                    </div>
                  </div>
                </li>
              ))}

              {/* Uploaded file rows — spinner cleared once upload completes */}
              {files.map((file) => (
                <li key={file.id} className="py-5 first:pt-0">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-background dark:bg-[hsl(var(--card-inset))]">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <p className="truncate text-base leading-[1.6] text-foreground dark:text-white">
                        {file.originalFilename}
                      </p>
                      <p className="text-sm leading-[1.5] text-muted-foreground">
                        {formatBytes(file.fileSize)}
                        {" · "}
                        {t("audienceCreate.addedOn", {
                          date: new Date(file.createdAt).toLocaleDateString(),
                        })}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <FileStatusBadge file={file} />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveClick(file)}
                        disabled={isRemoving}
                        aria-label={t("audienceCreate.removeFileAria", {
                          name: file.originalFilename,
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {file.indexingStatus === "failed" && file.indexingError && (
                    <div className="mt-2 flex items-start gap-2 rounded border border-destructive/30 bg-destructive/5 p-2 text-destructive">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <Text size="xs">{file.indexingError}</Text>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("audienceCreate.removeThisFile")}</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget
                ? t("audienceCreate.removeFileConfirm", {
                    name: removeTarget.originalFilename,
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>
              {t("audienceCreate.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                void confirmRemove();
              }}
              disabled={isRemoving}
            >
              {isRemoving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t("audienceCreate.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
